import ODAS from '@/utils/api/odas';
import ODRS from '@/utils/api/odrs';
import { STATUS_STARTED } from '@/components/pages/assessments/util';

const api = (server, api_key, user, locale = 'en') => {
  /**
   * Wrapper around functionality that checks for an active session before calling the given function.
   *
   * @param fn - function that returns a Promise
   * @returns Promise
   */
  const performIfLoggedIn = (fn) => (user?.id ? fn() : Promise.resolve({ skipped: 'not logged in' }));

  return {
    getAssessmentBySlug: (slug, agencyClientId, opts = {}) => {
      const url = agencyClientId ? `/api/agency_clients/${agencyClientId}/assessments/${slug}` : `/api/assessments/${slug}`;
      return ODAS.get(url, { locale, ...opts }, { server, api_key });
    },

    saveAssessmentAnswer: (args) => {
      return performIfLoggedIn(() => saveAnswer(args));
    },

    deleteAssessmentAnswer: ({ userAssessmentId, assessmentId, questionId, agencyClientId }) => {
      return performIfLoggedIn(() => deleteQuestionAnswer({ userAssessmentId, assessmentId, questionId, agencyClientId }));
    },

    saveAllAssessmentAnswers: (args) => {
      return performIfLoggedIn(() => saveAnswers(args));
    },

    fetchAnswersFromAssessment: (assessmentId, userAssessmentId, agencyClientId) => {
      return performIfLoggedIn(() => fetchAnswers(assessmentId, userAssessmentId, agencyClientId, locale));
    },

    loadResourcesForTags: async (tags, opts) => {
      try {
        const res = await fetchResourcesForTags(tags, opts, locale);
        return res?.data?.opportunities || [];
      } catch (ex) {
        console.error(ex); // TODO something!
      }
    },

    markAssessmentStarted: (assessmentId, agencyClientId, invitation) => {
      return performIfLoggedIn(() => updateStatus({ assessmentId, status: STATUS_STARTED, agencyClientId, invitation }));
    },

    restartAssessment: (assessmentId, agencyClientId, invitation) => {
      return performIfLoggedIn(() => updateStatus({ assessmentId, status: 'restart', agencyClientId, invitation }));
    },

    // calculate results, which is also responsible for setting user_assessment.status = 'finished'
    getAssessmentResults: (assessmentId, agencyClientId, invitation = {}, isIntake = false) => {
      let endpoint = agencyClientId
        ? `/api/agency_clients/${agencyClientId}/assessments/${assessmentId}/results`
        : `/api/assessments/${assessmentId}/results`;
      const params = [`locale=${encodeURIComponent(locale)}`];
      if (invitation.id && invitation.token) {
        params.push(`invitation_id=${encodeURIComponent(invitation.id)}`);
        params.push(`invitation_token=${encodeURIComponent(invitation.token)}`);
      }
      if (isIntake) {
        params.push('intake=true');
      }
      if (params.length > 0) {
        endpoint += `?${params.join('&')}`;
      }
      return ODAS.get(endpoint);
    },

    loadPublicAssessments: (options = {}) => {
      return ODAS.get('api/assessments/public', { locale, ...options });
    },

    // allow_duplicates is an option to include all the times a client has taken an assessment
    // The default limits to just returning one assessment of each time if the client has done it repeatedly
    loadUserAssessments: ({ agencyClientId, ...options }) => {
      let url = 'api/assessments';
      if (agencyClientId) {
        url = 'api/agency_clients/' + agencyClientId + '/assessments';
      }
      return ODAS.get(url, { locale, ...options });
    },

    getAgencyClient: async (agencyClientId) => {
      const url = `/api/agency_clients/${agencyClientId}`;
      return ODAS.get(url, { options: JSON.stringify({ include_completed_assessments: true }) }, { server, api_key });
    }
  };
};

const Assessments = {
  session: (user, locale = 'en') => api(false, null, user, locale),
  ssr: (api_key) => api(true, api_key),
  ...api(false)
};

function saveAnswer({ userAssessmentId, assessmentId, questionId, answer, agencyClientId = null }) {
  const question = {
    id: questionId,
    value: answer,
    origin_type: 'Assessment',
    origin_id: assessmentId
  };
  const endpoint = agencyClientId ? `/api/agency_clients/${agencyClientId}/question` : '/api/question';
  return ODAS.put(endpoint, { question, user_assessment_id: userAssessmentId }, {}, {});
}

function saveAnswers({ userAssessmentId, assessmentId, data, agencyClientId = null }) {
  const questions = data.map((answer) => {
    return {
      id: answer.questionId,
      value: answer.answer,
      origin_type: 'Assessment',
      origin_id: assessmentId
    };
  });
  const endpoint = agencyClientId ? `/api/agency_clients/${agencyClientId}/questions` : '/api/questions';
  return ODAS.put(endpoint, { questions, user_assessment_id: userAssessmentId }, {}, {});
}

function deleteQuestionAnswer({ userAssessmentId, questionId, agencyClientId }) {
  const endpoint =
    '/api/' + (agencyClientId ? `agency_clients/${agencyClientId}/` : '') + `/questions/${questionId}?user_assessment_id=${userAssessmentId}`;

  return ODAS.delete(endpoint);
}

// note that a "restart" status should now give you a new UserAssessment rather than changing "finished" to "started"
function updateStatus({ assessmentId, status, agencyClientId = null, invitation = null }) {
  const data = invitation?.id ? { invitation_id: invitation.id, invitation_token: invitation.token } : {};
  const endpoint = agencyClientId
    ? `/api/agency_clients/${agencyClientId}/assessments/${assessmentId}/${status}`
    : `/api/assessments/${assessmentId}/${status}`;
  return ODAS.put(endpoint, data, {}, {});
}

async function fetchAnswers(assessmentId, userAssessmentId, agencyClientId, locale) {
  const endpoint = agencyClientId ? `/api/agency_clients/${agencyClientId}/questions` : '/api/questions';
  try {
    const res = await ODAS.get(endpoint, { assessment_id: assessmentId, user_assessment_id: userAssessmentId, locale });
    return res?.data?.answers || {};
  } catch (ex) {
    console.error(ex);
    return {};
  }
}

/**
 * Query ODRS for the given tags
 *
 * @param {Array} activeTags - list of ODRS tags
 * @param {Object} opts
 * - TODO fill in the options
 */
async function fetchResourcesForTags(activeTags, opts = {}, locale = 'en') {
  const pageSearch = Object.assign({}, searchFromQuery(), {
    'query[tags]': formatTagsParameter(activeTags)
  });

  if (opts.activeProperties) {
    pageSearch['properties'] = opts.activeProperties;
    pageSearch['query[match]'] = 'properties';
  }

  if (opts.queryBounds) {
    pageSearch['query[lat]'] = opts.queryBounds.lat;
    pageSearch['query[long]'] = opts.queryBounds.lng;
  } else if (opts.userBounds) {
    const userBounds = opts.userBounds;
    pageSearch['query[bounds]'] = [
      userBounds.getSouthWest().lat(),
      userBounds.getSouthWest().lng(),
      userBounds.getNorthEast().lat(),
      userBounds.getNorthEast().lng()
    ].join(',');
    // In-map search results should be centered on the center of the map, not the original search lat/long.
    pageSearch['query[lat]'] = (userBounds.getSouthWest().lat() + userBounds.getNorthEast().lat()) / 2;
    pageSearch['query[long]'] = (userBounds.getSouthWest().lng() + userBounds.getNorthEast().lng()) / 2;
  }

  const opportunitySearch = searchParams(pageSearch, 'opportunities');
  // opportunitySearch.suppress_tags_plus = true
  opportunitySearch.per_page = opportunitySearch.per_page || opts.limit || 20;
  opportunitySearch.page = opts.page || 1;
  opportunitySearch.titles_only = typeof opts.titles_only !== 'undefined' ? opts.titles_only : true;
  opportunitySearch.attributes_to_include = opts.attributes_to_include || 'locations';

  opportunitySearch.locale = locale;
  return ODRS.search('opportunities', opportunitySearch);
}

// TODO implement angular Search.searchFromQuery
const searchFromQuery = () => {
  const searchObject = {};
  const qs = parseLocationParams();
  searchObject.page = qs.page || 1;
  searchObject['query[bounds]'] = qs.bounds || null;
  searchObject['query[lat]'] = qs.lat || Location.lat();
  searchObject['query[long]'] = qs.lng || Location.lng();
  searchObject['query[state]'] = qs.state || Location.state();
  searchObject['query[text]'] = qs.q;
  searchObject['query[tags]'] = qs.tags;
  searchObject['query[open]'] = qs.open;
  // ...
  return searchObject;
};

// TODO implement angular's Search.searchParams
const searchParams = (paramsObj, resourceTypeCollection, options = {}) => {
  const params = Object.assign({}, paramsObj);
  if (options.with_property_labels) {
    params['with_property_labels'] = 'true';
  }
  if (options.limit) {
    params['per_page'] = options.limit;
  }
  if (options.existingList && resourceTypeCollection) {
    if (options.resourceType && options.resourceIds) {
      params['ids'] = options.resourceIds.join(',');
    } else if (options.collection) {
      // const fetchables = ResourceHelpers.getFetchableIds(options.collection.fetchable_list_items);
      // params['ids'] = fetchables[resourceTypeCollection].join(',');
    }
  }
  if (params.properties) {
    Object.entries(params.properties).forEach(([key, value]) => {
      const k = `query[properties][${key}]`;
      if (value) params[k] = value;
      else delete params[k];
    });
    params['query[match]'] = 'by_type';
    delete params.properties;
  } else {
    delete params['query[match]'];
  }

  // In-map search should be centered on the center of the map, not the original search lat/long.
  if (params['query[bounds]']) {
    const bounds = params['query[bounds]'].split(',').map(Number);
    if (bounds.length === 4) {
      params['query[lat]'] = (bounds[0] + bounds[2]) / 2;
      params['query[long]'] = (bounds[1] + bounds[3]) / 2;
    }
  }

  return params;
};

const formatTagsParameter = (tags) => {
  // return tags.map(t => t[0].toUpperCase() + t.toLowerCase().slice(1)).join(',')
  return tags /*.map((t) => encodeURIComponent(t))*/
    .join(',');
};

// TODO this
const Location = {
  lat: () => '',
  lng: () => '',
  state: () => ''
};

// TODO correctly, perhaps from nextjs
const parseLocationParams = function () {
  const qs = {};
  (window?.location?.search || '')
    .replace(/^\?/, '')
    .split('&')
    .map((pair) => {
      const s = pair.split('=');
      qs[s[0]] = (s.length > 1 ? s[1] : '') || '';
    });
  return qs;
};

export default Assessments;
